import { formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { DashboardStats } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, PageHeader, RoleNotice, Spinner, StatCard, StatusBadge } from '../../ui/components';

const EOC_ROLES = ['DUTY_OFFICER', 'EOC_COMMANDER'];

function Breakdown({ title, counts }: { title: string; counts: Record<string, number> }) {
  const entries = Object.entries(counts);
  return (
    <div className="card p-4">
      <div className="mb-3 text-sm font-bold text-slate-900">{title}</div>
      <div className="space-y-2">
        {entries.length === 0 ? (
          <p className="text-sm text-slate-400">None on file.</p>
        ) : (
          entries.map(([key, count]) => (
            <div key={key} className="flex items-center justify-between text-sm">
              <StatusBadge status={key} />
              <span className="font-semibold tabular-nums">{count}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/** EOC command dashboard, "big-screen mode" — aggregate operational stats across alerts/shelters/incidents/relief/damage (M36). */
export function DrdmDashboard() {
  const { persona } = useAuth();
  const isEoc = persona ? persona.roles.some((r) => EOC_ROLES.includes(r)) : false;
  const statsQ = useApiQuery<DashboardStats>(isEoc ? '/api/v1/m36/dashboard' : null);
  const s = statsQ.data;

  return (
    <div>
      <PageHeader title="EOC Command Dashboard" subtitle="Aggregate operational stats across alerts, shelters, the incident board, relief applications and damage assessments (M36)." />

      {!isEoc ? <RoleNotice roles={EOC_ROLES} /> : null}
      {statsQ.error ? <ErrorNote message={statsQ.error} /> : null}
      {statsQ.loading ? <Spinner /> : null}

      {s ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="Shelter occupancy" value={`${s.shelterOccupancyTotal} / ${s.shelterCapacityTotal}`} />
            <StatCard label="Overdue taskings" value={s.overdueTaskings} tone={s.overdueTaskings > 0 ? 'bad' : 'good'} />
            <StatCard label="Pending resource requests" value={s.pendingResourceRequests} tone={s.pendingResourceRequests > 0 ? 'warn' : 'good'} />
            <StatCard
              label="Active EXTREME alerts"
              value={s.activeAlertsBySeverity.EXTREME ?? 0}
              tone={(s.activeAlertsBySeverity.EXTREME ?? 0) > 0 ? 'bad' : 'good'}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <Breakdown title="Active alerts by severity" counts={s.activeAlertsBySeverity} />
            <Breakdown title="Shelters by status" counts={s.sheltersByStatus} />
            <Breakdown title="Incidents by status" counts={s.incidentsByStatus} />
            <Breakdown title="Relief applications by status" counts={s.reliefApplicationsByStatus} />
            <Breakdown title="Damage assessments by severity" counts={s.damageAssessmentsBySeverity} />
          </div>

          <p className="text-xs text-slate-400">Generated {formatDateTime(s.generatedAt)}</p>
        </div>
      ) : null}
    </div>
  );
}
